import { useParams } from 'react-router-dom';
import { useMutation } from '@tanstack/react-query';
import authStore from '@stores/AuthStore';
import { KEYS, repository } from '@repositories/Repository';
import type { IOauthRepository } from '@repositories/oauthRepository/IOauthRepository';
import type { OAuthProvider, OAuthSignInDto } from '@repositories/oauthRepository/oauth.types';
import { isLimitedTokenResponse } from '@utils/authUtils';

const oauthRepo = repository.get<IOauthRepository>(KEYS.OAUTH_REPOSITORY);

/**
 * 새로운 통합 OAuth 로그인 Hook (백엔드 요구사항)
 */
export const useOAuthSignIn = () => {
  const { tenantId } = useParams();

  return useMutation({
    mutationFn: async ({
      provider,
      data,
      overrideTenantId,
    }: {
      provider: OAuthProvider;
      data: OAuthSignInDto;
      overrideTenantId?: string;
    }) => {
      const effectiveTenantId = overrideTenantId ?? tenantId;
      const result = await oauthRepo.oauthSignIn(provider, data, effectiveTenantId);
      return { user: result, effectiveTenantId };
    },
    onSuccess: ({ user, effectiveTenantId }) => {
      const isLimited = isLimitedTokenResponse(user);

      if (isLimited) {
        authStore.loginWithLimitedToken(user.id, user.name);
        return;
      }

      // 풀 토큰: effectiveTenantId가 이 사용자의 AP에 있을 때만 전달
      // (URL tenantId와 불일치 시 updateCurTenant에서 INVALID_TENANT throw 방지)
      const tenantIds = (user.additionalPermissions ?? [])
        .filter(ap => ap.resourceType === 'TENANT')
        .map(ap => ap.resourceId);
      const safeTenantId =
        effectiveTenantId && tenantIds.includes(effectiveTenantId)
          ? effectiveTenantId
          : undefined;

      authStore.login(
        user.id,
        user.name,
        user.additionalPermissions,
        safeTenantId,
      );
    },
    cacheTime: 0,
  });
};
